export const PROJECT_INFO = `
  ArgoCD Projects provide logical grouping and governance for applications, enabling multi-tenancy
  and security controls within your ArgoCD installation.

  **What are Projects?**
  Projects act as a security boundary and organizational unit in ArgoCD. They define what resources
  can be deployed, where they can be deployed, and who has access to manage them. Every application
  in ArgoCD must belong to a project.

  **Key Features:**

  **Source Restrictions** - Control which Git repositories can be used as sources
  - Whitelist trusted repositories for your team or organization
  - Prevent unauthorized code from being deployed

  **Destination Restrictions** - Control where applications can be deployed
  - Restrict deployments to specific clusters and namespaces
  - Prevent accidental deployment to production environments
  - Support wildcards for flexible namespace patterns (e.g., \`team-*\`)

  **Common Use Cases:**
  - **Team Isolation**: Separate projects for different teams (platform, data, frontend)
  - **Environment Separation**: Dedicated projects for dev, staging, and production
  - **Multi-tenancy**: Isolate different customers or business units
  - **Compliance**: Enforce security policies and audit requirements

  **Best Practices:**
  - Create dedicated projects for each application group

  For more details, refer to the [ArgoCD Projects Documentation](https://argo-cd.readthedocs.io/en/stable/user-guide/projects/).
`;

export const APPLICATION_NAME_INFO = `
  The **application name** serves as a unique identifier for your Deployment as a Service application. 
  It is used in various contexts such as naming the ArgoCD application, Kubernetes resources, and more. 
  Choosing a clear and descriptive name can help with organization and management of your applications.

  ℹ️ **Note**: If you are creating a **DaaS Application** for **KaaP**, we recommend using the same name as the **KaaP Tenant** to maintain consistency across systems.

  **Naming Rules:**
  - Must be unique across all applications
  - Can only contain lowercase letters, numbers, and hyphens
  - Must start with a letter
  - Must end with a letter or number
  - Maximum length of 46 characters

`;

export const APPLICATION_DESCRIPTION_INFO = `
  A brief description of the application. This is an optional field that can be used to provide additional context about the application, its purpose, or any other relevant information.
`;

export const OWNER_INFO = `
  The **owner** field specifies the team Supervisory Organization Group responsible for the application. 
  This information is important for accountability, communication, and access control purposes.
`;

export const CLUSTER_INFO = `
  Configure the destination Kubernetes cluster and namespace where ArgoCD will deploy your application.

  **Cluster Selection:**
  The cluster determines which Kubernetes environment will host your application. Available clusters
  are registered with ArgoCD and configured in your project's destination settings.

  **Cluster Name** - A friendly identifier for the Kubernetes cluster (e.g., "production-cluster", "dev-cluster")
  **Cluster Endpoint** - The API server URL of the Kubernetes cluster (e.g., "https://kubernetes.default.svc")

  **Namespace:**
  The namespace is the logical isolation boundary within the cluster where your application resources
  will be deployed. Each application should have its own namespace or share one with related services.

  **Namespace Restrictions:**
  - Only namespaces matching the allowed patterns defined in the ArgoCD project can be used
  - Patterns may include wildcards (e.g., \`team-*\`, \`app-prod-*\`)
  - If no namespaces are allowed for the selected cluster, check your project configuration
  - Invalid namespace selection will prevent application deployment

  For more details, refer to the [ArgoCD Application Specification](https://argo-cd.readthedocs.io/en/stable/user-guide/application-specification/).
`;

export const SOURCE_INFO = `
  The source defines where ArgoCD reads your application's manifests, Helm chart, or Kustomize overlay.

  ### Deployment type

  - **Manifests** — raw Kubernetes YAML in a Git repository.
  - **Helm** — a Helm chart, either from a Git repository, a Helm registry (both HTTPS and OCI are supported).
  - **Kustomize** — a Kustomize overlay in a Git repository.
`;

export const DEPLOYMENT_TYPE_INFO = `
  The deployment type controls how ArgoCD interprets the application source.
  Switching the type changes the rest of the form to match.

  - **Manifests** — raw Kubernetes YAML in a Git repository.
  - **Helm** — a Helm chart, from a Git repository, a public Helm registry (HTTPS), or an OCI registry (\`oci://\`).
  - **Kustomize** — a Kustomize overlay in a Git repository.
`;

export const MANIFESTS_CONFIG_INFO = `
  Configure the source for raw Kubernetes manifests.

  **Repository URL:** Git repository URL. Must match one of \`github.com/tomtom-internal/...\`
  or \`github.com/tomtom-international/...\`.

  **Revision:** branch, tag, commit SHA, or a semver constraint to track the latest matching tag (e.g. \`1.2.*\` or \`>=1.2.0 <1.3.0\`).

  **Manifests Path:** directory within the repository containing the raw
  YAML manifests.
`;

export const HELM_CONFIG_INFO = `
  Configure the source for a Helm chart deployment.

  **Repository URL:** Based on how the chart is stored, this can be one of the following:
  - the URL of the Helm registry,
  - the URL of the Git repository where the Helm chart source code is stored,
  - the OCI URL of the Helm chart. 
  
  Must match one of the [allowed URL prefixes](https://github.com/tomtom-internal/deployment-as-a-service/blob/main/docs/tools/allowlist.md).
  
  **Chart Version or Revision:** version tag of the chart, a semver constraint to track
  (e.g. \`1.2.*\`), or branch/tag/commit SHA when the chart comes from a Git source.

  **Chart Name:** _(Helm registry)_ chart name in the Helm registry.

  For \`oci://\` URLs, 
  the chart name is part of the URL. Chart name or path field is hidden when an OCI Helm
  repository is used.

  **Chart Path:** _(Helm from Git)_ directory within the repository containing
  \`Chart.yaml\`.

  **Release Name:** identifier for the Helm release in Kubernetes.
`;

export const KUSTOMIZE_CONFIG_INFO = `
  Configure the source for a Kustomize overlay.

  **Repository URL:** Git repository URL. Must match one of \`github.com/tomtom-internal/...\`
  or \`github.com/tomtom-international/...\`.

  **Revision:** branch, tag, commit SHA, or a semver constraint to track the latest matching tag (e.g. \`1.2.*\` or \`>=1.2.0 <1.3.0\`).

  **Kustomization Path:** directory within the repository containing
  \`kustomization.yaml\`.
`;

export const VALUES_CONFIG_INFO = `
  Configure Helm values to be passed to the chart.

  ### Values Source

  Optionally read value files from a secondary Git repository (\`tomtom-internal\`),
  instead of from the chart repo. Useful when deploying a centrally-managed
  Helm chart but keeping your own value files in your own repository.
  
  This uses ArgoCD's multi-source feature to attach the Git repository as a secondary source.
  The value file paths below are resolved against this repo.

  ### Value Files

  YAML files passed to Helm via \`--values\` to override the chart's defaults.
  Paths are relative to the chart location (or to the values-source repository,
  if enabled). Files apply in order — later files override earlier ones.

  ### Ignore Missing Value Files

  When enabled, ArgoCD skips value files that are not present instead of
  failing the sync. Useful for optional environment-specific values files.

  ### Values Overrides (ApplicationSet only)

  Helm values defined as a YAML object that reference ApplicationSet generator
  variables. Values defined here take precedence over values passed by values files.

  **When to use it**: when a Helm value must include an ApplicationSet template
  variable, such as \`{{.number}}\`, \`{{.head_sha}}\`, or \`{{.branch_slug}}\`.

  **When NOT to use it**: for static configuration. Static values belong in a
  value file in your repository (under "Value Files" above). This ensures
  changes go through your repository's review/CI processes and keeps a single
  source of truth.

  **Example (per-PR deployment):**
  \`\`\`yaml
  image:
    tag: '{{.head_sha}}'
  httpRoute:
    hostnames:
      - 'pr-{{.number}}.dev.example.com'
  \`\`\`

  **Note**: Do not forget to wrap generator variables in quotes to prevent YAML parsing issues

  For more details: [ArgoCD Helm source specification](https://argo-cd.readthedocs.io/en/stable/user-guide/helm/)
`;

export const SYNC_OPTIONS_INFO = `
  Fine-tune how ArgoCD synchronizes resources to your target cluster with sync options.

  **Sync options** control ArgoCD's behavior during deployment, offering precise control over 
  resource validation, pruning, deletion, and apply strategies.

  These options apply to all resources in the application. We recommend setting these options at
  Kubernetes-resource level for more granular control when possible. This can be done by adding the
  \`argocd.argoproj.io/sync-options\` annotation to specific resources in your Git repository.

  **Resource Management:**
  - \`Prune=false\` - Prevent resource deletion even when removed from Git
  - \`Delete=false\` - Retain resources when application is deleted (useful for PVCs)
  - \`PruneLast=true\` - Prune resources only after successful deployment of others

  **Validation & Apply:**
  - \`Validate=false\` - Skip kubectl validation (required for certain CRDs)
  - \`ServerSideApply=true\` - Use server-side apply instead of client-side (for large resources)
  - \`Replace=true\` - Use kubectl replace instead of apply (destructive, causes recreation)
  - \`Force=true\` - Force remove resources (dangerous, might cause inconsistency)

  **Performance & Safety:**
  - \`ApplyOutOfSyncOnly=true\` - Sync only changed resources (improves performance)
  - \`SkipDryRunOnMissingResource=true\` - Skip dry-run for unknown CRDs
  - \`FailOnSharedResource=true\` - Fail if resource managed by another application

  **Advanced:**
  - \`RespectIgnoreDifferences=true\` - Apply ignoreDifferences during sync (not just diff)
  - \`PrunePropagationPolicy=foreground\` - Control dependency deletion order (foreground, background, orphan)

  ⚠️ Options like \`Replace=true\` and \`Force=true\` can cause service disruption during sync.

  For a full list of sync options, refer to the [ArgoCD Sync Options Documentation](https://argo-cd.readthedocs.io/en/stable/user-guide/sync-options/).
`;

export const AUTO_SYNC_INFO = `
  Enable ArgoCD to automatically deploy changes when it detects differences between Git and your cluster.

  **Auto Sync** continuously monitors your Git repository and automatically syncs changes to the cluster 
  without manual intervention. This eliminates the need for CI/CD pipelines to directly access ArgoCD's API.

  **How it works:**
  - ArgoCD checks for changes every 1 to 2 minutes
  - When differences are detected between Git and cluster state, sync is triggered automatically
  - Only syncs when application is OutOfSync (not when Synced or in error state)
  - Won't retry failed syncs against the same commit unless self-heal is enabled

  **Auto Sync Options:**

  **Prune** - Automatically delete resources removed from Git
  - When disabled (default): Resources remain in cluster even after removal from Git
  - When enabled: Keeps cluster in perfect sync with Git by removing obsolete resources
  - Safety: Prevents accidental deletion of all resources when Git is empty (unless allowEmpty is enabled)

  **Self Heal** - Automatically correct manual changes made in the cluster
  - When disabled (default): Manual kubectl changes persist until next Git commit
  - When enabled: ArgoCD reverts any manual cluster changes back to Git state within ~5 seconds
  - Use case: Enforce strict GitOps - prevent configuration drift from manual interventions

  **Allow Empty** - Permit sync when no resources are found in Git
  - When disabled (default): Protects against accidentally deleting all resources
  - When enabled: Allows application to have zero resources (removes all existing resources)
  - Requires prune to be enabled to have effect

  **Recommendations:**
  - **Development**: Enable auto-sync for rapid iteration
  - **Production**: Consider manual sync for controlled deployments, or enable with self-heal for strict GitOps
  - **Prune**: Enable cautiously - ensure you understand resource lifecycle implications
  - **Self-heal**: Great for preventing drift, but disables kubectl-based troubleshooting

  ⚠️ Rollback is not possible with auto-sync enabled. Disable auto-sync to perform rollbacks.

  For more details, refer to the [ArgoCD Auto Sync Documentation](https://argo-cd.readthedocs.io/en/stable/user-guide/auto_sync/).
`;

export const AUTO_CREATE_NAMESPACE_INFO = `
  Adds the \`CreateNamespace=true\` sync option to the application, and configures labels and annotations
  for the automatically created namespace.

  **How it works:**
  - Enable automatic namespace creation with this configuration
  - Optionally define labels and annotations in Managed Namespace Metadata
  - ArgoCD automatically creates the namespace specified in the destination cluster configuration, if it doesn't already exist
  
  **Existing namespaces:**
  - If adopting an existing namespace with metadata, upgrade it to server-side apply first
  - Without this upgrade, ArgoCD may remove existing labels/annotations
  - See [Kubernetes Server-Side Apply](https://kubernetes.io/docs/reference/using-api/server-side-apply/#upgrading-from-client-side-apply-to-server-side-apply)

  **Namespace manifests take precedence:**
  - If your app includes a Namespace manifest, it overrides the namespace metadata specified here
  - Direct Namespace resources in Git always win over sync policy metadata
  - Remove the Namespace manifest from your app if you want the sync policy to control metadata

  **Common use cases:**
  - Apply governance labels (cost center, team, environment)
  - Add annotations for monitoring tools (e.g., Prometheus) or config synchronizers (e.g., kubed/config-syncer)

  **Important notes:**
  - By default, auto-created namespaces are NOT tracked or owned by ArgoCD (won't be deleted when the Application is deleted)
  - This option only creates the namespace specified in the application's destination configuration. It does not create namespaces specified in other Kubernetes manifests in your repository
  - If the application destination is a KaaP cluster, a label indicating the tenant name will be added automatically

  ⚠️ **Use with Caution:**
  Namespaces are cluster-scoped and may be shared across applications.
  If each application is deployed to its own dedicated namespace, we recommend creating namespace manifests 
  directly in your Git repository instead of using this configuration.

  For more details, refer to the [ArgoCD Create Namespace Sync Option Documentation](https://argo-cd.readthedocs.io/en/latest/user-guide/sync-options/#create-namespace).
`;

export const APPLICATION_METADATA_INFO = `
  Add labels and annotations to your ArgoCD Application for filtering and advanced features.

  **Labels:**
  - Used for filtering and categorization in ArgoCD UI
  - Constraints:
    - Label keys and values must contain 63 characters or less. Allowed characters are letters, numbers, \`-\`, \`.\`, \`_\`, and they must start and end with an alphanumeric character. Keys may also carry an optional DNS-subdomain prefix (e.g. \`tomtom.com/my-label-key\`). See [Kubernetes label syntax](https://kubernetes.io/docs/concepts/overview/working-with-objects/labels/#syntax-and-character-set).
  - Examples:
    - \`environment: production\`
    - \`criticality: high\`
    - \`tomtom.com/my-label-key: my-value\` 

  **Annotations:**
  - Mostly intended to be used for advanced ArgoCD features **(use with caution)**
  - Constraints:
    - Annotation keys must contain 63 characters or less with an optional DNS-subdomain prefix. Allowed characters are letters, numbers, \`-\`, \`.\`, \`_\`, and it must start and end with an alphanumeric character. See [Kubernetes annotation syntax](https://kubernetes.io/docs/concepts/overview/working-with-objects/annotations/#syntax-and-character-set).
    - Annotation values have no restrictions, any string can be used.
  - Examples:
    - \`argocd.argoproj.io/compare-options\`
    - \`argocd.argoproj.io/manifest-generate-paths\`

  **Where does this metadata get added?**
  - For _DaaS Applications_, metadata is added directly to the ArgoCD Application resource
  - For _DaaS ApplicationSets_, it is added to Applications created by the ApplicationSet.
  Each generated Application inherits this metadata. ApplicationSet template variables are allowed
  in annotation and label values.

  **Best Practices:**
  - Use consistent naming (e.g., \`kebab-case\`) and keep values short and descriptive
  - Use **labels** for filtering and categorization
  - ⚠️ **Consult PE before adding annotations** - incorrect annotations can cause 
    unexpected behavior, sync failures, or operational issues

  For more information, refer to the [ArgoCD Application Specification](https://argo-cd.readthedocs.io/en/stable/user-guide/application-specification/)
`;

export const GENERATORS_INFO = `
  Define generators to automatically create multiple ArgoCD Applications from a single ApplicationSet.

  **What are Generators?**
  Generators produce sets of parameters that are rendered into the ApplicationSet template, automatically 
  creating one Application for each parameter set. This enables powerful patterns like deploying the same 
  application to multiple clusters or environments without duplicating configuration.

  **Available Generator Types:**

  **List Generator** - Deploy based on a static list of parameters
  - Manually define key-value pairs for each application
  - Simple and explicit, good for fixed deployments
  - Example: Deploy to specific teams or predefined environments

  **Cluster Generator** - Deploy to all registered ArgoCD clusters
  - Automatically discovers clusters managed by ArgoCD
  - Responds to cluster addition/removal events
  - Example: Deploy monitoring to every cluster automatically

  **Git Generator** - Create applications based on Git repository structure
  - File-based: Generate from files matching a pattern (e.g., \`config-*.yaml\`)
  - Directory-based: Generate from repository directory structure
  - Example: Self-service - teams create apps by committing config files

  **Matrix Generator** - Combine two generators (cartesian product)
  - Create all possible combinations of parameters from two sources
  - Example: Deploy all apps to all clusters (apps × clusters)

  **Merge Generator** - Merge parameters from multiple generators
  - Override base generator values with additional generators
  - Control merge order and precedence

  **Pull Request Generator** - Deploy for each open pull request
  - Automatically create preview environments for PRs
  - Clean up when PR is closed or merged

  **YAML Format:**
  Generators are defined in YAML format. Each generator type has its own schema.

  **Example - List Generator:**
  \`\`\`yaml
  generators:
    - list:
        elements:
          - cluster: dev
            url: https://dev-cluster.example.com
          - cluster: prod
            url: https://prod-cluster.example.com
  \`\`\`

  **Example - Cluster Generator:**
  \`\`\`yaml
  generators:
    - clusters:
        selector:
          matchLabels:
            environment: production
  \`\`\`

  **Best Practices:**
  - Start with simple generators (List or Cluster) before complex ones
  - Use filters to limit which parameters are generated
  - Test with a small parameter set before scaling
  - Combine generators (Matrix/Merge) for advanced patterns

  ⚠️ **Important**: Invalid YAML will prevent ApplicationSet creation. Use the editor's validation to catch errors.

  For detailed documentation on each generator type, refer to the [ArgoCD ApplicationSet Generators Documentation](https://argo-cd.readthedocs.io/en/stable/operator-manual/applicationset/Generators/).
`;

export const TEMPLATE_PATCH_INFO = `
  Apply custom modifications to the ApplicationSet template that generates ArgoCD Applications for your project.

  **What is a Template Patch?**
  A template patch allows you to customize the ArgoCD Application template used by the ApplicationSet. 
  The patch is merged with the base template, enabling you to add or override specific fields without 
  replacing the entire template configuration.

  ℹ️ **Note**: This is an advanced feature. Most users won't need to patch the template. 
  Use only when default template configuration doesn't meet your specific requirements.
  
  **How it works:**
  - You provide a YAML patch that follows the ArgoCD Application structure
  - The patch is merged with the base ApplicationSet template
  - Fields you specify override or extend the base template
  - Unspecified fields remain unchanged from the base template
  - The merge follows YAML merge semantics (deep merge for objects, replace for arrays)

  **Format Requirements:**
  
  ⚠️ **All template patches must follow this format:**
  - First line must be: \`templatePatch: |\`
  - All content lines must have **at least 2 spaces indentation**
  - Empty lines are allowed
  
  The editor will automatically position your cursor with the correct indentation when you add a template patch.

  **Template Structure:**

  The patch content should follow the standard ArgoCD Application specification:
  \`\`\`yaml
  templatePatch: |
    metadata:
      labels: {}
      annotations: {}
      finalizers: []
    spec:
      syncPolicy: {}
      ignoreDifferences: []
      info: []
  \`\`\`

  **Common Use Cases:**

  **1. Add Custom Labels**
  \`\`\`yaml
  templatePatch: |
    metadata:
      labels:
        team: data-platform
        cost-center: engineering
        compliance: required
  \`\`\`

  **2. Add Finalizers**
  \`\`\`yaml
  templatePatch: |
    metadata:
      finalizers:
        - resources-finalizer.argocd.argoproj.io
  \`\`\`

  **3. Set Ignore Differences**
  \`\`\`yaml
  templatePatch: |
    spec:
      ignoreDifferences:
        - group: apps
          kind: Deployment
          jsonPointers:
            - /spec/replicas
  \`\`\`

  **4. Configure Sync Policy**
  \`\`\`yaml
  templatePatch: |
    spec:
      syncPolicy:
        automated:
          prune: true
          selfHeal: true
        syncOptions:
          - CreateNamespace=true
  \`\`\`

  **Best Practices:**
  - Only patch fields you need to customize
  - Keep patches minimal and focused
  - Maintain proper YAML indentation (2 spaces minimum for all content)
  - Avoid patching fields managed by generators (like \`source\`, \`destination\`)
  - Test your patch in a non-production environment first

  **Security Considerations:**
  - Patches apply to all applications generated by the ApplicationSet
  - Be cautious with automated sync policies (prune, selfHeal)
  - Validate YAML syntax before applying
  - Review changes with your team before deploying to production

  **What NOT to Patch:**
  - \`spec.source\` - Managed by generators
  - \`spec.destination\` - Managed by generators
  - \`spec.project\` - Managed by system
  - \`metadata.name\` - Generated from template variables

  **Validation Errors:**
  - **"Template patch must start with 'templatePatch: |' on the first line"** - Add the required header
  - **"Line X must have at least 2 spaces indentation"** - Ensure all content lines start with 2+ spaces
  - Check that you haven't accidentally removed the header or indentation when editing

  **Troubleshooting:**
  - **Patch not applied?** Check YAML syntax, structure, and indentation
  - **Validation error?** Ensure first line is \`templatePatch: |\` and content has 2+ spaces indentation
  - **Application sync fails?** Verify patched fields are valid for ArgoCD Applications
  - **Need to see base template?** Check ApplicationSet values in [Deployment as a Service repository](https://github.com/tomtom-internal/deployment-as-a-service/tree/main/gitops/projects)

  For more details, refer to:
  - [ArgoCD Application Specification](https://argo-cd.readthedocs.io/en/stable/operator-manual/application.yaml)
  - [ArgoCD ApplicationSet Template](https://argo-cd.readthedocs.io/en/stable/operator-manual/applicationset/Template/)
`;

export const IMAGE_UPDATER_INFO = `
  Configure **ArgoCD Image Updater** to automatically detect new container image versions in your
  registry and update your application's git source so ArgoCD picks up the change.

  **What is Image Updater?**
  Image Updater is a controller that polls your image registry, compares the tags against an
  update strategy you define, and — when a newer version matches — writes the new tag back to
  the git repository that holds your application manifests. ArgoCD then syncs the update like
  any other GitOps change.

  **How it fits into the DaaS form:**
  - Toggle **Enable Image Updater** to activate the configuration block
  - Provide a **Git Write-back Configuration** describing where the new tag should be committed
  - Provide an **Images** YAML describing which images to track and how to update them

  **Prerequisites:**
  - The git repository in **Write-back Repository** must allow the **integration-argocd** GitHub
    App to commit to the branch specified in **Write-back Branch**
  - The image references in the **Images** section must be from an allowed registry:
    - \`artifactory.tomtomgroup.com\` — reachable by default
    - Azure Container Registry instances (\`*.azurecr.io\`) — you must grant the
      **daas-argo** service principal the \`AcrPull\` and \`Container Registry Repository Reader\`
      roles on your ACR
    - Public registries (e.g. Docker Hub, GHCR, quay.io)

    See the [DaaS ArgoCD documentation](https://frontstage.tomtomgroup.com/docs/default/repository/deployment-as-a-service/docs/tools/argocd/#offered-capabilities)
    for the full list of offered capabilities.
  - For Helm-based apps, your chart must expose the image name/tag (or full spec) as values
    that match the paths in \`manifestTargets.helm\`

  **Common Use Cases:**
  - Automatically promote new tags matching \`^v\\d+\\.\\d+\\.\\d+$\` to staging
  - Track the latest \`main\` digest of an internal service in a dev environment
  - Roll forward across patch versions without manual PRs

  ⚠️ **Important:**
  - Variables (\`{{ … }}\`) from ApplicationSet generators are **not** allowed in the write-back
    config — the controller needs a static repository and revision
  - Disabling the toggle clears the entire configuration

  For more details, refer to the [ArgoCD Image Updater Documentation](https://argocd-image-updater.readthedocs.io/en/stable/).
`;

export const GIT_WRITE_BACK_INFO = `
  Tell ArgoCD Image Updater where to commit updated image references. Without this, the
  controller has nowhere to write the new tag and the update is dropped.

  **Fields:**

  **Repository** - The git repository where your manifests / Helm values live.
  - Pick from your project's registered repositories (free-typing is not allowed)
  - Must be a **static** URL — ApplicationSet template variables like \`{{ .cluster }}\` are
    rejected, since Image Updater runs as a separate controller and does not resolve
    ApplicationSet templates
  - Auto-filled from the Repositories section above when you enable Image Updater

  **Revision** - The branch (or tag/ref) to commit against.
  - Typically your default branch (\`main\`, \`master\`) or an environment-specific branch
    (\`env/dev\`, \`env/prod\`)
  - Must be a static value with only letters, numbers, hyphens, dots, underscores or slashes
  - Auto-filled from the repo revision when available

  **Write-back Target** - Optional path that tells the controller *how* to update the file.
  - Leave blank for the controller's default behavior (annotations on the ArgoCD Application)
  - For Helm value files, use \`helmvalues:<path-to-values.yaml>\`
  - For Kustomize overlays, use \`kustomization:<path-to-overlay-directory>\`
  - The path is resolved relative to the git repository root

  ℹ️ **Note:** The controller currently only supports the **git** write-back method (commits via
  ArgoCD's git credentials). The form fixes \`method: git\` for you.

  ⚠️ **Important:**
  - The write-back identity (**integration-argocd**) must have **write** access
  - If the repo is protected (required reviewers, status checks), integration-argocd needs to be able to bypass the branch protection rules to write to that branch.

  For more details, refer to the [Write-Back Methods Documentation](https://argocd-image-updater.readthedocs.io/en/stable/basics/update-methods/).
`;

export const IMAGES_INFO = `
  Declare the container images to track, as a free-form YAML list. Each entry tells Image
  Updater which image to watch, how to pick the next version, and where to write the change.

  The DaaS helm chart consumes these entries and rewrites them into the ArgoCD Image Updater
  spec, deriving the alias and adding registry-specific pull secrets for you.

  **Required field per image:**
  - \`name\` - The image reference to track (e.g. \`nginx\`, \`ghcr.io/org/api\`). The chart
    derives the Image Updater \`alias\` from this value automatically.

  **Optional \`commonUpdateSettings\` block per image** (matches the structure the chart writes
  into the Image Updater spec):
  - \`updateStrategy\` - How to pick the next tag (chart default: \`latest\`). Common values:
    - \`semver\` - newest SemVer tag matching your version constraint
    - \`newest-build\` - newest tag by registry build timestamp
    - \`digest\` - track the latest digest of a mutable tag (e.g. \`main\`)
    - \`alphabetical\` - newest tag by lexicographic sort (use sparingly)
    - \`latest\` - legacy strategy name (chart default)
  - \`forceUpdate\` - Update even when the new image isn't strictly newer (default \`false\`)
  - \`allowTags\` - Restrict eligible tags. Must include the ArgoCD Image Updater
    match-function prefix \`regexp:\` (e.g. \`regexp:^v\\d+\\.\\d+\\.\\d+$\`)
  - \`ignoreTags\` - A list of tags to skip even if they would otherwise match
  - \`platforms\` - Target platform(s) the controller inspects when resolving tags
    (default \`[linux/amd64]\`). Image Updater only reads build/digest metadata for these
    platforms, so if your image is built for **arm64** you must set this explicitly
    (e.g. \`[linux/arm64]\`) or no matching tag will be found

  **Other optional fields per image:**
  - \`manifestTargets\` - Where the new tag is written inside your manifests; forwarded
    verbatim to Image Updater:
    - Helm: \`helm: { name: image.repository, tag: image.tag }\` (or \`spec\` for a single
      full-image path)
    - Kustomize: \`kustomize: { name: <image to replace> }\`

  **YAML example:**
  \`\`\`yaml
  images:
    - name: ghcr.io/org/api
      commonUpdateSettings:
        updateStrategy: semver
        allowTags: 'regexp:^v\\d+\\.\\d+\\.\\d+$'
        ignoreTags:
          - latest
      manifestTargets:
        helm:
          name: image.repository
          tag: image.tag
    - name: ghcr.io/org/worker
      commonUpdateSettings:
        updateStrategy: digest
        allowTags: 'regexp:^main$'
        platforms:
          - linux/arm64
      manifestTargets:
        helm:
          name: worker.image.repository
          tag: worker.image.tag
  \`\`\`

  **YAML validation:**
  The editor parses your YAML on the fly and surfaces:
  - Parse errors with the offending line/column (red counter)
  - Schema warnings — missing required fields or wrong types (orange counter)

  ℹ️ **Auto-derived fields:** You don't provide \`alias\` (computed from \`name\`) or
  \`pullSecret\` (the chart selects the right secret based on the registry host — currently
  Azure Container Registry and TomTom Artifactory).

  ⚠️ **Important:**
  - Each \`name\` must be unique within the list — duplicates result in the last one winning
  - Keep the **same \`manifestTargets\` type for every image** in the list. If the first image
    uses \`manifestTargets.helm\`, all other entries must also use \`helm\`; the same applies for
    \`kustomize\`. Mixing types within a single application isn't supported because the
    underlying manifests are either Helm values or Kustomize overlays, not both
  - When using \`digest\` strategy, \`allowTags\` must resolve to a single tag (commonly \`regexp:^main$\`)
  - Aggressive strategies like \`alphabetical\` on a mixed-tag repo can pick unintended tags

  For more details, refer to the [Image Configuration Documentation](https://argocd-image-updater.readthedocs.io/en/stable/configuration/images/)
  and the [Update Strategies Documentation](https://argocd-image-updater.readthedocs.io/en/stable/basics/update-strategies/).
`;

export const APPSET_APP_NAME = `
  Specify the name pattern for applications created by the ApplicationSet using generator parameters.

  **Template Syntax:**
  Application names can use template variables from your generators using the \`{{parameter}}\` syntax.
  Each generator produces parameters that can be referenced to create unique, descriptive application names.

  **How it works:**
  - Define parameters in your generators (e.g., \`appName\`, \`appEnv\`, \`cluster\`, \`team\`)
  - Reference them in the application name using double curly braces
  - ArgoCD creates one application per generator output, substituting the values

  **Examples:**

  **Simple naming:**
  \`\`\`
  Generator output: { appName: "my-service", appEnv: "dev" }
  Name template: {{appName}}-{{appEnv}}
  Result: my-service-dev
  \`\`\`

  **Multi-cluster deployment:**
  \`\`\`
  Generator output: { app: "frontend", cluster: "eu-west" }
  Name template: {{app}}-{{cluster}}
  Result: frontend-eu-west
  \`\`\`

  **Team-based naming:**
  \`\`\`
  Generator output: { service: "api", team: "platform", env: "prod" }
  Name template: {{team}}-{{service}}-{{env}}
  Result: platform-api-prod
  \`\`\`

  **Best Practices:**
  - Keep names lowercase with hyphens (Kubernetes naming conventions)
  - Ensure uniqueness across all generated applications
  - Avoid special characters except hyphens and dots

  ⚠️ **Important**: All referenced parameters must exist in your generator output, or application creation will fail.

  For more details, refer to the [ArgoCD ApplicationSet Templates Documentation](https://argo-cd.readthedocs.io/en/stable/operator-manual/applicationset/Template/).
`;
